/**
 * @fileoverview Organization guide — the Hans Academy content for org admins.
 *
 * The third audience, and the last to get a guide. Instructors have GuidePage,
 * students and parents StudentGuidePage; an org admin was left reading the
 * instructor guide, which documents a portal they cannot sign in to and omits
 * every surface they actually use. Shares the searchable UI in
 * `components/GuideView`, so all three read identically.
 *
 * Same scope rule as the other two: only what production ships. Worth knowing
 * while editing —
 *
 *   - The org roster IMPORT is real (it posts through `orgApi`), unlike the
 *     instructor-side import page, which is a mockup with no route. So bulk
 *     import is documented here and nowhere else.
 *   - A class's meetings are authored here and held in Kalver Scheduler; the
 *     instructor's Hans calendar only mirrors them. Nothing in this guide
 *     should suggest an org schedules inside the instructor's portal.
 *   - No plan is named and no price quoted, the same rule GuidePage keeps: the
 *     pricing surfaces state both once, and a guide repeating them is one more
 *     place every reprice has to be chased.
 *
 * Screenshots live in `public/academy/` and are captured from the real org
 * portal by `scripts/capture-academy.mjs`. Re-run it after a UI change.
 */

import {
  Rocket,
  Users,
  Building2,
  CalendarDays,
  CreditCard,
  FileText,
  ShieldCheck,
  Settings,
  HelpCircle,
} from "lucide-react";
import GuideView from "@/components/GuideView";
import { supportEmail } from "@/lib/companyBrand";

/** Shorthand for an image in `public/academy`. Mirrors GuidePage's helper. */
const shot = (file, alt, caption) => ({
  src: `/academy/${file}`,
  alt,
  caption,
  kind: file.endsWith(".gif") ? "gif" : "image",
});

/* ------------------------------------------------------------------ */
/*  Organization guide content                                         */
/* ------------------------------------------------------------------ */

const orgGuideCategories = [
  {
    id: "getting-started",
    icon: Rocket,
    title: "Getting Started",
    sections: [
      {
        id: "what-is-the-org-portal",
        title: "What the organization portal is",
        content:
          "If you run a music school, an after-school program, or a company that contracts instructors, the organization portal is your side of Hans. You build programs and their classes, staff them with instructors, watch what is actually being taught, and account for it — while each instructor keeps their own portal and their own private students.",
        media: shot(
          "org-dashboard.png",
          "The Hans organization dashboard: sessions taught, active programs, instructors teaching and students, over the chart for the chosen range",
          "Organization dashboard"
        ),
      },
      {
        id: "creating",
        title: "Creating your organization",
        steps: [
          "Go to the organization sign-up page and enter your organization's name, your name, your email and a time zone",
          "Enter the 6-digit code we email you — it proves the address is yours",
          "Connect your Kalver Scheduler business, under Settings → Schedule, so classes can be scheduled",
          "Set your branding: your name, your logo, and the name your emails come from",
          "Invite your instructors",
        ],
        note: "The admin email is verified before the portal opens. Existing organizations are asked to verify too, once, the first time they sign in after that requirement shipped.",
      },
      {
        id: "navigation",
        title: "Finding your way around",
        content: "The sidebar follows the order a director usually needs things:",
        items: [
          { label: "Dashboard", desc: "Your reporting: what was taught, by whom and for whom, over any range, with a CSV of every tab" },
          { label: "Instructors", desc: "Everyone teaching for you, plus pending invitations" },
          { label: "Active students", desc: "The students counted this month, and what they cost" },
          { label: "Calendar", desc: "Every class meeting across every program, a week at a time" },
          { label: "Programs", desc: "Your programs, their classes, staffing and applications" },
          { label: "Settings", desc: "Pinned to the bottom: things you set once and revisit rarely, Notifications among them" },
        ],
      },
      {
        id: "settings",
        title: "Settings",
        content:
          "Settings opens a short list of sections, each a page of its own, with what is yours first (You), then the organization's. On any of them, the Settings link at the top goes back to the list, and the section's name beside it opens a menu of the others.",
        items: [
          { label: "Preferences", desc: "The emails you receive, your own password, and deleting your account" },
          { label: "Appearance", desc: "Light or dark, on this device" },
          { label: "Plan", desc: "The organization's Hans plan, its add-ons and what it covers" },
          { label: "Admins", desc: "Who can administer the organization" },
          { label: "Branding", desc: "Logo, display name, accent colour, and your custom domain" },
          { label: "Features", desc: "Which Hans features your instructors have by default, and the rules for substitute requests" },
          { label: "Notifications", desc: "What your instructors are told by email and text, and when program emails go out" },
          { label: "Schedule", desc: "Your Kalver Scheduler connection, connected classes and consent at booking" },
          { label: "Activity", desc: "An append-only record of who changed what" },
        ],
      },
      {
        id: "scheduler",
        title: "Connecting Kalver Scheduler",
        content:
          "Class meetings live in Kalver Scheduler; Hans authors them there and mirrors them back. Connect the business once under Settings → Schedule — the Calendar's Scheduling preferences button opens it too — and the connection is then something you read approximately never. Services, hours and payment settings open in Scheduler itself.",
      },
    ],
  },
  {
    id: "instructors",
    icon: Users,
    title: "Instructors",
    sections: [
      {
        id: "inviting",
        title: "Inviting an instructor",
        content:
          'Instructors → enter their email address → invite. They see the invitation in their own Hans account and accept or decline it. You cannot attach an instructor to your organization: joining means agreeing to let you see their teaching in your programs, so it has to be their decision.',
        note: "Someone who has only been invited shows as provisional and cannot be given a class yet — a pending invitation is not acceptance, and a class would attribute teaching to a person who never agreed to teach here.",
      },
      {
        id: "instructor-detail",
        title: "One instructor's record",
        content:
          "Each instructor's page shows what they teach for you: their students, their lessons in your programs, consent coverage across that roster, and the per-instructor feature overrides. It does not show their private students, and it never shows their own notes.",
      },
      {
        id: "features",
        title: "Feature policy",
        content:
          "Features, under Settings, sets the default on/off state of a handful of Hans features for everyone in the organization — the public booking page, scheduling, the repertoire assistant, whether instructors may add their own students, and classes. Per-instructor exceptions live on that instructor's page.",
        note: "A policy governs what an instructor may do under your organization. It cannot reach into their private teaching.",
      },
      {
        id: "viewers",
        title: "Read-only viewers",
        content:
          "A sponsor, a school coordinator or a department head can be given a viewer account: they sign in at the organization portal but every administrative route refuses them, and they see only the programs you name. Every viewer sees a program's classes and calendar; everything else starts unticked and is granted one at a time. When you add them you choose how their invitation goes: Share a link to send from your own email, or Send from Hans; nothing is sent, and they are not added, until you choose. They set their own password from it, then sign in under Organization, like you. Each program opens like your dashboard, with tabs. Depending on what you tick, it shows its numbers (sessions held and to come, students enrolled, the week ahead), attendance, the lesson notes instructors shared with families (never notes for a student under 13, which go to parents only), instructor contacts and the roster; they can upload a class roster, and export what they see. The weekly recap and week-ahead emails are separate ticks. A viewer can switch either email off for themselves, or leave a program, which your Activity records. Their portal carries your logo and name, as yours does, beside each program too.",
      },
      {
        id: "removing",
        title: "When someone leaves",
        content:
          "Remove an instructor from the Instructors list, or they can leave from their own Programs page. Either way your visibility into their teaching ends immediately and any policy you set is cleared from their account. Lessons already taught in your programs stay — they are your record as much as theirs.",
      },
    ],
  },
  {
    id: "programs",
    icon: Building2,
    title: "Programs & Classes",
    sections: [
      {
        id: "creating-a-program",
        title: "Creating a program",
        content: "A program is a body of teaching you run. Three kinds, because they behave differently:",
        items: [
          { label: "Private lessons", desc: "Families book an instructor directly, in Scheduler" },
          { label: "Class", desc: "The program runs classes whose meetings you schedule" },
          { label: "Camp", desc: "Classes over a fixed run — an end date is required" },
        ],
        media: shot(
          "org-program-detail.png",
          "A program page in the Hans organization portal, showing its instructors, classes and teaching totals",
          "A program"
        ),
      },
      {
        id: "visibility",
        title: "Who can see a listing",
        content:
          "A program listing is either public — any Hans instructor can see it and apply — or private, visible only to the email addresses you list. Applications arrive on the program page with the note the applicant wrote, and you accept or decline. Once a program is staffed, mark it filled from its Edit dialog on the Programs page and it stops taking applications; reopen it the same way.",
      },
      {
        id: "classes",
        title: "Classes and who teaches them",
        content:
          "A class is owned by the instructor who teaches it, because every roster, shared lesson and teaching record keys off that owner. Naming one is optional — a term is usually built before it is staffed — and you can assign or reassign the owner later without rewriting taught history. Substitutes cover individual meetings without taking the class over.",
        note: "The instructors offered are your organization's own, not only the program's: assigning a class to one of them joins them to that program.",
      },
      {
        id: "schedule",
        title: "Giving a class its term",
        content:
          "A class's meetings come from either a weekly recurrence — as many weekday-and-time pairs as the class meets, so \"Tuesdays at 4 and Thursdays at 5\" is one term — or a plain list of dates for a term that doesn't recur cleanly.",
        items: [
          { label: "Preview first", desc: "Every date is shown before anything is written; what you approve is what lands" },
          { label: "Holidays up front", desc: "No-class days are excluded when the term is built, not cancelled one at a time later" },
          { label: "The school calendar goes in", desc: "Drop in the district's .ics file or paste a column of dates, into either the meeting days or the no-class days" },
          { label: "Re-applying replaces", desc: "Applying a new schedule replaces the old one rather than adding to it — two overlapping terms would double every meeting" },
        ],
        note: "A holiday that falls on a day the class never meets is reported back to you, because it is almost always the wrong day.",
      },
      {
        id: "roster",
        title: "Filling the roster",
        content:
          "Paste the roster or drop in a CSV — first name, last name, and an email where there is one. Every row is resolved and shown before a single write: each says whether the student will be created or is already this instructor's student. An import never rewrites an existing student's name, address or consent.",
        note: "Importing is not consent. Students created this way land with none, badged \"Imported · needs review\", and cannot be recorded until a family actually answers. Consent sits beside every name on the class page because it decides whether the class can be recorded at all.",
        media: shot(
          "org-class-detail.png",
          "A class page in the Hans organization portal with its schedule, roster and each student's consent state",
          "A class"
        ),
      },
      {
        id: "emails",
        title: "What a program sends",
        content:
          "Each program decides whether it mails a weekly recap and a week-ahead note, and to whom. The organization sets the day and hour those go out, on its Notifications page; the program says whether it sends at all and who is on the list.",
      },
      {
        id: "export",
        title: "The record of a term",
        content:
          "A program's term downloads as a spreadsheet, one row per meeting: date and time, class, instructor, any substitute, whether it was taught, cancelled or still owes notes, attendance as a count beside the roster size, minutes, and the lesson summary.",
        note: "Attendance is a count, never a list of names. Who was in the room is a child's attendance record, and a file that leaves the organization is the last place it belongs. Transcripts and instructors' private notes are never exported.",
      },
    ],
  },
  {
    id: "calendar",
    icon: CalendarDays,
    title: "Your Week",
    sections: [
      {
        id: "org-calendar",
        title: "The organization calendar",
        content:
          "Calendar opens on this week: every class meeting across every program, with who is teaching it, any substitute covering it, and anything called off. Switch to a week grid or a month, and filter by program or instructor — a filtered week says so, so it is never mistaken for a quiet one. Scheduling preferences, at the top, opens your Kalver Scheduler connection.",
        note: "A private-lessons program is named rather than left out. Its schedule is individual bookings in Scheduler, which Hans cannot enumerate, and silently omitting it would read as \"that program has nothing on\".",
      },
      {
        id: "notes-owed",
        title: "Lessons that still need writing up",
        content:
          "A meeting that was taught but never written up shows as owing notes — on the class, in the program export, and to the instructor in their own portal. Cancelled meetings never count as owed: nothing was taught, so nothing is owed.",
      },
      {
        id: "substitute-terms",
        title: "Letting instructors request a substitute",
        content:
          "Substitute requests are off until you turn them on. Settings → Features sets the default for every class and camp program; a program's Settings tab can use it or set its own. You decide how much notice a request needs, how many requests an instructor can make in a program before they should talk to you instead, and whether they must leave a lesson plan.",
        note: "A request inside the notice period, or past the limit, is never sent: the instructor is shown the program's contacts instead, and you can still assign cover yourself from the class.",
      },
      {
        id: "substitute-requests",
        title: "Approving a request",
        steps: [
          "Every administrator is emailed when an instructor asks, with a link to the class",
          "Open the program: Substitute requests lists what is waiting, soonest first, with the reason and the plan",
          "Approve and choose who covers — someone on your substitute roster, one of your instructors (they don't need to be on the program), or confirm later",
          "Or decline, with a reason. Either way the instructor is emailed",
        ],
      },
      {
        id: "substitute-covering",
        title: "When a substitute covers a session",
        content:
          "A substitute from your roster is emailed the session — date and time, venue and room, the program's contacts, your note — and a private link to read the plan and write the session's notes. The link works until the program ends; Send a fresh link replaces it. One of your instructors covering sees the session in their own Hans portal instead. The session's notes count as a lesson for your organization, tagged as taught by a substitute: read them on the class page, write them yourself if you like, and send them to families when you're happy with them.",
        note: "The regular instructor still sees the session, greyed out, but it isn't theirs: it never asks them for notes, doesn't count toward their teaching, and they can't send it to anyone.",
      },
    ],
  },
  {
    id: "billing",
    icon: CreditCard,
    title: "Active Students & Plan",
    sections: [
      {
        id: "active-students",
        title: "The count that produces the bill",
        content:
          "Organizations are billed per active student, so the count is shown before the invoice is, with the names behind it. The month's state is on the badge: a running count while the month is open, settling once it closes and late activity can still arrive, and final when the figure an invoice is raised against is frozen.",
        media: shot(
          "org-usage.png",
          "The active students page in the Hans organization portal, showing the month's count, its state and the students behind it",
          "Active students"
        ),
      },
      {
        id: "usage-tabs",
        title: "The four explanations",
        content: "The month, its state and the headline numbers stay pinned above four tabs, each answering one question:",
        items: [
          { label: "Students", desc: "Who was counted this month" },
          { label: "Programs", desc: "Which programs they were counted in" },
          { label: "Costs", desc: "How the count becomes money" },
          { label: "Minutes", desc: "The AI recording minutes the headcount buys" },
        ],
      },
      {
        id: "plan",
        title: "Your plan",
        content:
          "The organization buys the platform rather than a number of seats, so there is no quantity to choose, nothing to hand out and no cap to run into. Plan, under Settings, shows what you pay, when it renews, and who it covers; Active students shows the arithmetic behind it.",
      },
      {
        id: "instructors-covered",
        title: "What coverage means for an instructor",
        content:
          "An instructor your plan covers doesn't pay for Hans, and the recording minutes they use inside your programs count against the organization rather than against them. Their private teaching stays theirs, on their own plan.",
      },
    ],
  },
  {
    id: "reporting",
    icon: FileText,
    title: "Reporting & Records",
    sections: [
      {
        id: "reporting",
        title: "Your dashboard",
        content:
          "The dashboard is your reporting. Four tiles lead — sessions taught, active programs, instructors teaching and students — and each opens the tab with the rows behind it: Overview, Programs, Instructors, Students and Lessons. Choose the Overview's range (the past 30 days, the past 12 months, this year, last year, all time, or dates of your own) and the tiles and chart follow it.",
      },
      {
        id: "exports",
        title: "Getting your data out",
        content:
          "Export, under the tabs, downloads whatever tab is open as a CSV: the overview for your chosen range, the program and instructor tables, every student on your rosters, or every session across your programs. Transcripts and instructors' private notes are never in it.",
      },
      {
        id: "counting",
        title: "How sessions are counted",
        content:
          "A session is one taught meeting. A class session counts once however many students attended — a class lesson is a single shared record — so the reach figure is reported separately rather than inflating the session count.",
      },
      {
        id: "activity",
        title: "The activity record",
        content:
          "Activity, under Settings, is an append-only ledger of administrative changes, written as sentences: who invited whom, who turned a feature off for which instructor, when a program changed. It is not editable, by anyone, which is what makes it worth having.",
      },
      {
        id: "consent-coverage",
        title: "Consent coverage",
        content:
          "Each instructor's page reports what fraction of their roster has granted recording consent, with unreviewed imported students counted separately so they don't flatter the number, and a class roster shows each family's answer. It is the figure to watch before a term starts: a class cannot be recorded until its families have answered.",
      },
    ],
  },
  {
    id: "boundaries",
    icon: ShieldCheck,
    title: "What You Can and Cannot See",
    sections: [
      {
        id: "attribution",
        title: "Teaching in your programs, and nothing else",
        content:
          "An organization sees the lessons taught in its own programs. It does not see an instructor's private students, their other organizations, or anything they teach outside your programs — and attribution follows the schedule, so a lesson belongs to your program because it was taught on your program's time.",
      },
      {
        id: "not-visible",
        title: "Never visible to an organization",
        items: [
          { label: "Instructor notes", desc: "An instructor's private notes on a student are theirs alone" },
          { label: "Transcripts", desc: "Never shown and never exported" },
          { label: "Lesson audio", desc: "Full lesson recordings are deleted after processing succeeds or permanently fails. Audio awaiting retry is retained temporarily, with a seven-day cleanup backstop. Consented practice clips are kept separately for up to 30 days." },
          { label: "Attendance by name", desc: "Reported to you as a count, never as a list of children" },
        ],
      },
      {
        id: "consent-is-the-familys",
        title: "Consent belongs to the family",
        content:
          "Nothing an organization does grants consent. Not a roster handed over, not a booking, not a signed contract with a school. The family answers a request sent to them, they can withdraw it at any time from any summary email, and every request, grant, decline and withdrawal is kept in the instructor's ledger.",
      },
      {
        id: "membership",
        title: "Membership is the instructor's choice",
        content:
          "An instructor accepts your invitation, and can leave whenever they decide to. That is deliberate: your visibility into their teaching exists because they agreed to it, and the moment they don't, it ends.",
      },
    ],
  },
  {
    id: "admin",
    icon: Settings,
    title: "Admins & Branding",
    sections: [
      {
        id: "admins",
        title: "Who can administer the organization",
        content:
          "Admins, under Settings, lists everyone with administrative access. Add one and they set their own password from an emailed link that works for 30 days; until they do they show as Invited, and Resend invite emails a fresh link. Deactivate one and their live sessions end immediately. You cannot deactivate yourself — someone has to be able to get back in.",
      },
      {
        id: "branding",
        title: "Wearing your own name",
        content:
          "Branding sets the logo, the display name your instructors and families see, the from-name on your emails, and your accent colour. The logo takes effect the moment it uploads; everything else saves together and the portal re-brands live.",
        media: shot(
          "org-branding.png",
          "The branding page in the Hans organization portal, with logo upload, display name and accent colour",
          "Branding"
        ),
      },
      {
        id: "custom-domain",
        title: "Your own address",
        content:
          "The custom-domain setup lives on the same page, because an organization that wants its name on the booking page is the same one that wants its name in the URL.",
      },
      {
        id: "appearance",
        title: "Light or dark",
        content:
          "The portal ships both. Switch it in Settings, under You › Appearance; the choice is saved on that device, so your laptop and a shared front-desk machine stay independent.",
      },
    ],
  },
  {
    id: "help",
    icon: HelpCircle,
    title: "Help & FAQ",
    sections: [
      {
        id: "faq-invite-not-accepted",
        title: "An instructor hasn't accepted their invitation",
        content:
          "They show as provisional until they do, and cannot be given a class. Ask them to check the email address you invited — the invitation reaches their own Hans account, so it has to be the address they signed up with. You can invite again from the Instructors page.",
      },
      {
        id: "faq-empty-calendar",
        title: "A program has nothing on the calendar",
        content:
          "If it is a private-lessons program, that's expected: its bookings live in Scheduler and are named rather than drawn. For a class program, check the class has been given a schedule — a class created without one has no meetings yet.",
      },
      {
        id: "faq-cannot-record",
        title: "An instructor says they can't record a class",
        content:
          "Every member of a class needs recording consent before a class recording will start. Open the class and look at the consent column: an imported roster begins with none, and each family has to answer their own request.",
      },
      {
        id: "faq-count-changed",
        title: "The active-student count changed after the month ended",
        content:
          "A closed month sits in a settling state while late activity can still arrive, then freezes as final. The badge on the page tells you which state you are looking at, and only the final figure is invoiced.",
      },
      {
        id: "faq-contact",
        title: "How do I get help?",
        content: `Email ${supportEmail()} and a person will answer. If the question is about one instructor's teaching, they can usually answer faster than we can.`,
      },
    ],
  },
];

const OrgGuidePage = () => (
  <GuideView
    categories={orgGuideCategories}
    title="Hans Academy"
    intro="Your guide to the organization portal — programs, classes, instructors, and the record of what was taught. Search below or browse by topic."
  />
);

export default OrgGuidePage;
